import { ApiError, InputError } from './errors.ts';
import { me } from './session.ts';

/**
 * The idempotency key of a request the carrier may still be working on (#219). A carrier that
 * reviews before it answers (Seguros G&T, ADR-0019) leaves an issuance under review
 * (ISSUANCE_PENDING) or an endorsement REFERRED: asking again with the SAME key follows that
 * request; a new key presents another one, and the carrier would get two solicitudes. So the
 * console keeps one key per request (the same quote, version, carrier and option; the same
 * endorsement of the same policy) until it finishes: accepted, refused, or failed for good.
 *
 * The keys live in the tab's sessionStorage (a reload in the same session still follows the
 * request), tagged with the person who sent them, and are forgotten when the session ends.
 * Without storage (a private window that refuses it) they live in this page only.
 */

/** Where the keys are kept in sessionStorage. */
export const RETRY_KEYS = 'nubi.retry-keys';

export type RetryKeyStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
type Kept = { member: string; keys: Record<string, string> };

let storage: () => RetryKeyStorage | null = () => {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
};
/** What this page keeps when the storage refuses (or before it is read). */
let memory: Kept | null = null;

/** Another storage (tests; a reload is a new module state over the same storage). */
export function setRetryKeyStorage(s: RetryKeyStorage) {
  storage = () => s;
  memory = null;
}

function read(): Kept {
  const member = me?.memberId ?? '';
  try {
    const raw = storage()?.getItem(RETRY_KEYS);
    const kept = raw ? (JSON.parse(raw) as Kept) : null;
    // Only the keys of the person signed in: another person's request is never followed.
    if (kept && kept.member === member && kept.keys && typeof kept.keys === 'object')
      return (memory = { member, keys: { ...kept.keys } });
    if (raw !== null && raw !== undefined) return (memory = { member, keys: {} });
  } catch {
    // Storage refused or held something unreadable: what this page remembers.
  }
  return memory?.member === member ? memory : (memory = { member, keys: {} });
}

function write(kept: Kept) {
  memory = kept;
  try {
    const s = storage();
    if (!s) return;
    if (Object.keys(kept.keys).length) s.setItem(RETRY_KEYS, JSON.stringify(kept));
    else s.removeItem(RETRY_KEYS);
  } catch {
    // A private window: the key still lives in this page.
  }
}

/** The key of the request in course for `scope`, or a new one (kept until it finishes). */
function keyFor(scope: string): string {
  const kept = read();
  const existing = kept.keys[scope];
  if (existing) return existing;
  const key = crypto.randomUUID();
  write({ ...kept, keys: { ...kept.keys, [scope]: key } });
  return key;
}

/** The request of `scope` finished: the next one is a new request with a new key. */
function finish(scope: string) {
  const kept = read();
  if (!(scope in kept.keys)) return;
  const { [scope]: _done, ...keys } = kept.keys;
  write({ ...kept, keys });
}

/** Forgets every request in course (the session ended: nothing passes to the next person). */
export function forgetRetryKeys() {
  memory = null;
  try {
    storage()?.removeItem(RETRY_KEYS);
  } catch {
    // Nothing kept to forget.
  }
}

/**
 * Whether a failure leaves the request in course: the carrier still reviews it, another call
 * with the key is at the carrier, or the outcome is unknown (no answer, a timeout, the carrier
 * or the server failed). Anything else (a refusal, a field to fix) ended it.
 */
function stillInCourse(e: unknown): boolean {
  if (e instanceof InputError) return false;
  if (!(e instanceof ApiError)) return true;
  if (e.code === 'ISSUANCE_PENDING' || e.code === 'IN_PROGRESS') return true;
  return e.status >= 500 || e.status === 408 || e.status === 429;
}

/**
 * Sends the request of `scope` with its key: the same key while it is in course, so a retry
 * follows the same request. `finished` says whether an answer ends it (an endorsement still
 * REFERRED does not); by default any answer does.
 */
export async function followRequest<T>(
  scope: string,
  send: (idempotencyKey: string) => Promise<T>,
  finished: (answer: T) => boolean = () => true,
): Promise<T> {
  const key = keyFor(scope);
  let answer: T;
  try {
    answer = await send(key);
  } catch (e) {
    if (!stillInCourse(e)) finish(scope);
    throw e;
  }
  if (finished(answer)) finish(scope);
  return answer;
}

/** One issuance: the quote's version, the carrier and the option chosen. */
export const issuanceScope = (p: {
  quoteId: string;
  version: number;
  carrier: string;
  option: string;
}) => `issue:${JSON.stringify([p.quoteId, p.version, p.carrier, p.option])}`;

/** One endorsement: the policy (its schedule) and exactly what changes, from when. */
export const endorsementScope = (scheduleId: string, request: unknown) =>
  `endorse:${JSON.stringify([scheduleId, request])}`;
